import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useAuth } from '@/hooks/useAuth'
import { tvTemplateService } from '@/services/tv_template_service'
import { TvTemplateVod } from '@/hooks/useTvTemplates'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'

interface VodDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: number
  vod: TvTemplateVod | null
  onSuccess?: () => void
}

const formSchema = z.object({
  vod_name: z.string().min(1, '請輸入影片名稱').max(100, '影片名稱不能超過100個字符'),
  source: z.string().min(1, '請輸入影片來源').max(200, '影片來源不能超過200個字符'),
  broadcast_type: z.string().min(1, '請選擇播放類型'),
  order: z.coerce.number().int().optional(),
  is_active: z.boolean().default(true),
})

type FormValues = z.infer<typeof formSchema>

export function VodDialog({
  open,
  onOpenChange,
  templateId,
  vod,
  onSuccess,
}: VodDialogProps) {
  const { token } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditing = !!vod

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vod_name: vod?.vod_name || '',
      source: vod?.source || '',
      broadcast_type: vod?.broadcast_type || 'tv',
      order: vod?.order || undefined,
      is_active: vod?.is_active ?? true,
    },
  })

  useEffect(() => {
    if (!open) {
      form.reset({
        vod_name: '',
        source: '',
        broadcast_type: 'tv',
        order: undefined,
        is_active: true,
      })
    } else if (vod) {
      form.reset({
        vod_name: vod.vod_name || '',
        source: vod.source || '',
        broadcast_type: vod.broadcast_type || 'tv',
        order: vod.order || undefined,
        is_active: vod.is_active ?? true,
      })
    }
  }, [open, vod, form])

  const onSubmit = async (values: FormValues) => {
    if (!token) return

    setIsSubmitting(true)

    try {
      let result

      if (isEditing && vod) {
        result = await tvTemplateService.updateTemplateVod(
          templateId,
          vod.template_vod_id,
          {
            vod_name: values.vod_name,
            source: values.source,
            order: values.order,
            is_active: values.is_active,
          },
          token,
        )
      } else {
        result = await tvTemplateService.createTemplateVod(
          templateId,
          {
            vod_name: values.vod_name,
            source: values.source,
            broadcast_type: values.broadcast_type,
            order: values.order,
            is_active: values.is_active,
          },
          token,
        )
      }

      if (result.success) {
        onOpenChange(false)
        onSuccess?.()
      } else {
        toast.error(result.message || '請稍後再試')
      }
    } catch {
      toast.error('發生錯誤，請稍後再試')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? '編輯影片' : '新增影片'}</DialogTitle>
          <DialogDescription>
            {isEditing ? '修改模板影片資訊' : '新增影片到模板'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="vod_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>影片名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入影片名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="source"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>影片來源</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入影片來源網址" {...field} />
                  </FormControl>
                  <FormDescription>請輸入影片的來源網址，例如 YouTube 連結</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="broadcast_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>播放類型</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isEditing}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="選擇播放類型" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="tv">電視影片</SelectItem>
                      <SelectItem value="clinic_vod">診間影片</SelectItem>
                      <SelectItem value="health_vod">衛教影片</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>排序</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="請輸入排序數字"
                      {...field}
                      value={field.value === undefined ? '' : field.value}
                    />
                  </FormControl>
                  <FormDescription>數字越小，排序越前面</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>啟用狀態</FormLabel>
                    <FormDescription>啟用後，此影片將會在設備播放</FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isEditing ? '更新中...' : '新增中...'}
                  </>
                ) : isEditing ? (
                  '更新'
                ) : (
                  '新增'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
